import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Shield, Scroll, Lock } from "lucide-react";

const PRIMARY = "#6B2D5C";
const SECONDARY = "#EC1F7C";

const PAGES = {
  mentions: {
    icon: FileText,
    title: "Mentions légales",
    intro: "Informations légales obligatoires concernant le site officiel de l'Istres Provence Handball.",
    sections: [
      { h: "Éditeur du site", p: "Istres Provence Handball — Association Loi 1901\nHalle Polyvalente — Avenue de Galliéni, 13800 Istres, France\nSIRET : à compléter\nNuméro RNA : à compléter\nDirecteur de la publication : le Président du Club" },
      { h: "Hébergement", p: "Site hébergé sur infrastructure cloud sécurisée managée par WeCockpit.\nOpérateur technique : Agence MSC × WeCockpit." },
      { h: "Propriété intellectuelle", p: "L'ensemble des contenus présents sur ce site (textes, photos, vidéos, logos) sont la propriété exclusive de l'Istres Provence Handball ou de ses partenaires (LNH, sponsors, joueurs). Toute reproduction non autorisée est strictement interdite." },
      { h: "Crédits", p: "Données sportives officielles fournies par la Ligue Nationale de Handball (LNH).\nConception, design et développement : Agence MSC × WeCockpit." },
    ],
  },
  reglement: {
    icon: Scroll,
    title: "Règles du club",
    intro: "Charte d'engagement et règles de bonne conduite à la Halle Polyvalente d'Istres et autour du club.",
    sections: [
      { h: "Esprit handball", p: "Respect des arbitres, des joueurs adverses, des bénévoles et de l'ensemble du public. Le club promeut les valeurs du sport : solidarité, dépassement, fair-play et inclusion." },
      { h: "Accès à la salle", p: "L'accès aux matchs nécessite un billet valide. La direction se réserve le droit de refuser ou expulser toute personne en état d'ébriété, agressive ou portant des objets dangereux. Les fumigènes, banderoles non autorisées et messages discriminatoires sont interdits." },
      { h: "Tribunes & ambiance", p: "Les chants, tambours et drapeaux sont les bienvenus tant qu'ils restent respectueux et ne mettent pas en danger les autres spectateurs. Les enfants restent sous la responsabilité de leurs parents." },
      { h: "Photo & vidéo", p: "L'utilisation à but personnel est tolérée. Toute exploitation commerciale (médias, reportages) nécessite une accréditation préalable du service communication du club." },
    ],
  },
  confidentialite: {
    icon: Lock,
    title: "Politique de confidentialité",
    intro: "Comment nous collectons, utilisons et protégeons vos données personnelles.",
    sections: [
      { h: "Données collectées", p: "Nous collectons uniquement les données nécessaires à la fourniture de nos services (réservation, billetterie, newsletter, espace partenaire, formulaires de contact) : nom, prénom, email, téléphone et données de navigation anonymisées." },
      { h: "Finalités", p: "Vos données sont utilisées pour : gérer votre relation avec le club, vous transmettre des informations sportives et commerciales (avec votre consentement), améliorer le site, et respecter les obligations légales." },
      { h: "Durée de conservation", p: "Les données sont conservées 3 ans après le dernier contact actif, sauf obligations légales spécifiques (comptabilité, billetterie)." },
      { h: "Vos droits (RGPD)", p: "Vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition. Pour exercer ces droits : rgpd@istreshandball.com." },
      { h: "Cookies", p: "Le site utilise uniquement des cookies essentiels (session, panier) et de mesure d'audience anonyme. Aucun cookie publicitaire tiers n'est déposé sans votre consentement." },
    ],
  },
  cgv: {
    icon: Shield,
    title: "CGV — Conditions Générales de Vente",
    intro: "Conditions applicables aux achats réalisés sur la boutique officielle Istres Provence Handball.",
    sections: [
      { h: "Articles vendus", p: "Maillots officiels, équipements d'entraînement, cadeaux et accessoires de la collection officielle 2025-2026, ainsi que les billets et abonnements pour les matchs à domicile." },
      { h: "Prix & paiement", p: "Les prix sont indiqués en euros, TTC. Paiement sécurisé par carte bancaire ou virement. Une facture est envoyée par email après confirmation de commande." },
      { h: "Livraison", p: "Livraison en France métropolitaine sous 5 à 10 jours ouvrés. Les commandes peuvent également être retirées gratuitement à la boutique du club lors des matchs à domicile." },
      { h: "Retours & échanges", p: "Vous disposez de 14 jours après réception pour retourner un article (hors articles personnalisés au nom/numéro). Les frais de retour sont à votre charge sauf défaut produit." },
      { h: "Service client", p: "Pour toute question : boutique@istreshandball.com." },
    ],
  },
};

export default function SportLegalPage() {
  const { kind } = useParams();
  const page = PAGES[kind] || PAGES.mentions;
  const Icon = page.icon;

  return (
    <div className="bg-white" data-testid={`sport-legal-${kind || "default"}`}>
      {/* Hero */}
      <section className="relative overflow-hidden py-16 sm:py-20 text-white"
               style={{ background: `linear-gradient(135deg, #160732 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: `radial-gradient(ellipse at 80% 0%, rgba(236,31,124,0.4), transparent 50%)` }}/>
        <div className="relative max-w-4xl mx-auto px-4 lg:px-8">
          <Link to="/sport" className="inline-flex items-center gap-2 text-white/70 hover:text-pink-400 transition text-xs font-black tracking-widest uppercase">
            <ArrowLeft size={14}/> Retour à l'accueil
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-2xl shrink-0"
                 style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
              <Icon size={26}/>
            </div>
            <div>
              <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">Informations</div>
              <h1 className="font-blackhawk text-4xl sm:text-5xl uppercase italic leading-tight mt-1">{page.title}</h1>
              <p className="text-white/70 max-w-2xl mt-3 text-sm sm:text-base leading-relaxed">{page.intro}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 space-y-10">
          {page.sections.map((s, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="border-l-4 pl-6 py-1"
              style={{ borderColor: SECONDARY }}
            >
              <h2 className="font-blackhawk text-2xl uppercase italic" style={{ color: PRIMARY }}>{s.h}</h2>
              <p className="text-slate-600 leading-relaxed mt-3 whitespace-pre-line text-[15px]">{s.p}</p>
            </motion.article>
          ))}

          <div className="pt-8 border-t border-slate-200 text-xs text-slate-400 italic">
            Document mis à jour pour la saison 2025-2026. Pour toute question juridique, contactez secretariat@istreshandball.com.
          </div>
        </div>
      </section>
    </div>
  );
}
